import axios from "axios";
import { Navigate } from "react-router-dom";
import toast from "react-hot-toast";
import { createKybUploadedDocApi, uploadDocApi, uploadFileApi, verifymalewareFileApi } from "./api";
import moment from "moment-timezone";

export const wipeCookies = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("account_id");
};

export const SEGMENT_TO_LOGIN = {
  fiu: "/fiu-login",
  compliance: "/compliance-login",
  internal: "/internal-login",
  accounting: "/accounting-login",
  otc: "/otc-login",
  "external-accounting": "/external-accounting-login",
  taas: "/taas-login",
  transaction: "/transaction-login",
  kybOnboarding: "/kybOnboarding-login",
  nonIndianKyb: "/nonIndianKyb-login",
  otcNonIndia: "/otcNonIndia-login",
  apiDashboard: "/apiDashboard-login",
  iplogs: "/iplogs-login",
  ledger: "/ledger-login",
  edd: "/edd-login",
  kyc_review: "/kycreview-login",
  "admin-2fa": "/admin-2fa/login",
};

export const getLoginPathForPathname = (pathname = window?.location?.pathname || "/") => {
  const firstSegment = pathname.split("/").filter(Boolean)[0] || "";
  return SEGMENT_TO_LOGIN[firstSegment] || "/";
};

export const logoutHandler = () => {
  wipeCookies();
  window.location.href = getLoginPathForPathname();
};

export const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("token");

  if (token) return children;

  const loginPath = getLoginPathForPathname();

  return <Navigate to={loginPath} replace />;
};

export const errorReaderToast = (error) => {
  if (
    typeof error?.response?.data !== "object" ||
    error?.response?.data === null
  ) {
    console.error("Provided data is not a valid object");
    toast.error(error.message);
    return;
  }

  Object.entries(error?.response?.data).forEach(([key, messages]) => {
    if (Array.isArray(messages)) {
      // Handle case where messages is an array
      messages.forEach((message) => {
        toast.error(`${key}: ${message}`);
      });
    } else if (typeof messages === "string") {
      // Handle case where messages is a single string
      toast.error(`${key}: ${messages}`);
    } else {
      console.error(
        `Expected a string or an array of messages for key "${key}", but got ${typeof messages}`
      );
    }
  });
};

export const removeEmptyKeys = (obj) => {
  if (Array.isArray(obj)) {
    // Recursively process each item in the array
    return obj.map(removeEmptyKeys);
  } else if (typeof obj === "object" && obj !== null) {
    // Create a new object to avoid mutating the original object
    const cleanedObj = {};
    Object.keys(obj).forEach((key) => {
      if (key === "key") delete cleanedObj["key"];
      const cleanedValue = removeEmptyKeys(obj[key]);
      if (cleanedValue !== "" && cleanedValue !== null) {
        cleanedObj[key] = cleanedValue;
      }
    });
    return cleanedObj;
  }
  // Return the original value if it's not an object or array
  return obj;
};

export const isEmptyObject = (obj) => {
  return (
    typeof obj === "object" && obj !== null && Object.keys(obj).length === 0
  );
};

export const resetFiuData = {
  ref_no: "",
  mobile_numbers: [],
  aadhaar: [],
  pan: [],
  email: [],
  bank_accounts: [],
  address: [],
  passport: [],
  voter_id: [],
  driving_license: [],
  cin: [],
  gstin: [],
  tan: [],
  wallet_addresses: [],
  sender_wallet_addresses: [],
  receiver_wallet_addresses: [],
  transaction_hashes: [],
};

export const createObjectNameS3 = ({
  user_id,
  doc_type,
  owner_type,
  entity_id,
  url_for,
  file_type,
  affiliated_entity_id = null,
  doc_sub_type,
}) => {
  const timeEpoch = Date.now();
  switch (url_for) {
    case "c_o_i":
    case 'coi':
      return `onboarding/${user_id}/coi/coi_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'pan':
      return `onboarding/${user_id}/pan/pan_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'aadhaar':
      return `onboarding/${user_id}/aadhaar/${doc_type}_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'passport_front':
      return `onboarding/${user_id}/passport/${owner_type}/passport_front_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'passport_back':
      return `onboarding/${user_id}/passport/${owner_type}/passport_back_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'pan_shareholder':
    case 'pan_director':
    case 'pan_ubo':
    case 'pan_source_declaration_signatory':
      return `onboarding/${user_id}/pan/${owner_type}/pan_id_${affiliated_entity_id}_t_${timeEpoch}.${file_type}`;
    case 'aadhaar_shareholder':
    case 'aadhaar_ubo':
    case 'aadhaar_director':
    case 'aadhaar_source_declaration_signatory':
      return `onboarding/${user_id}/aadhaar/${owner_type}/${doc_type}_id_${affiliated_entity_id}_t_${timeEpoch}.${file_type}`;
    case 'address_proof_source_declaration_signatory':
    case 'address_proof_shareholder':
    case 'address_proof_ubo':
    case 'address_proof_director':
    case 'address_proof_authorized_person':
      return `onboarding/${user_id}/address_proof/${owner_type}/address_proof_id_${affiliated_entity_id}_t_${timeEpoch}.${file_type}`;
    case 'video_declaration_wallet':
    case 'video_declaration_director':
    case 'source_of_funds':
    case 'wallet_declaration':
    case 'sanctions_doc':
    case 'financial_statement':
      return `onboarding/${user_id}/${doc_type}/${doc_type}_id_t_${timeEpoch}.${file_type}`;
    case 'fiu_aml_policy':
      return `onboarding/${user_id}/fiu_registration/fiu_aml_policy_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'share_certificate':
      return `onboarding/${user_id}/share_certificate/share_certificate_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'ownership_structure':
      return `onboarding/${user_id}/ownership_structure/ownership_structure_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'licenses':
      return `onboarding/${user_id}/licenses/licenses_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'aoa':
      return `onboarding/${user_id}/aoa/aoa_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'ubo_doc':
      return `onboarding/${user_id}/ubo_doc/ubo_doc_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'moa':
      return `onboarding/${user_id}/moa/moa_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'br':
      return `onboarding/${user_id}/br/br_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'gstin':
      return `onboarding/${user_id}/gstin/gstin_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'kyc_form':
      return `onboarding/${user_id}/kyc_form/kyc_form_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'shareholding_doc':
      return `onboarding/${user_id}/shareholding/shareholding_doc_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'director_doc':
      return `onboarding/${user_id}/director_doc/director_doc_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'bank_statement':
      return `onboarding/${user_id}/bank_statement/bank_statement_id_${affiliated_entity_id || user_id}_t_${timeEpoch}.${file_type}`;
    case 'ubo':
      return `onboarding/${user_id}/ubo/ubo_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'fignate_screenshot':
      return `onboarding/${user_id}/fiu_registration/fignate_screenshot_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'fiu_email_confirmation':
      return `onboarding/${user_id}/fiu_registration/fiu_email_confirmation_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'fiu_proof_date_of_initial_registration':
      return `onboarding/${user_id}/fiu_registration/fiu_proof_date_of_initial_registration_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'fiu_non_applicability_declaration':
      return `onboarding/${user_id}/fiu_registration/fiu_non_applicability_declaration_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'net_worth_certificate':
      return `onboarding/${user_id}/networth/networth_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'financial_statement_for_fiu':
      return `onboarding/${user_id}/financial_statement_for_fiu/financial_statement_for_fiu_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'bank_statement_for_fiu':
      return `onboarding/${user_id}/bank_statement_for_fiu/bank_statement_for_fiu_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'aadhaar_front':
      return `onboarding/${user_id}/aadhaar/aadhaar_front_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'aadhaar_back':
      return `onboarding/${user_id}/aadhaar/aadhaar_back_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'aadhaar_front':
      return `onboarding/${user_id}/aadhaar/aadhaar_front_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'operating_address_proof':
      return `onboarding/${user_id}/operating_address_proof/operating_address_proof_id_${user_id}_t_${timeEpoch}.${file_type}`;
    case 'others': {
      const safeSubType = String(doc_sub_type || "other_doc")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_-]/g, "_");
      return `onboarding/${user_id}/others/${safeSubType}_id_${user_id}_t_${timeEpoch}.${file_type}`;
    }
    default:
      if (doc_type) {
        return `onboarding/${user_id}/${doc_type}/${doc_type}_id_${user_id}_t_${timeEpoch}.${file_type}`;
      }
      break;
  }
};

export const saveDocHandler = async ({
  email,
  doc_type,
  doc_sub_type,
  owner_type,
  objName,
  affiliated_entity_id,
  setSaveResponse,
  reference_id,
  doc_id
}) => {
  if (!email) {
    toast.error("Email not selected");
    return;
  }
  if (!objName) return;
  try {
    let dataT = {
      email: email,
      doc_type: doc_type,
      doc_sub_type: doc_sub_type,
      owner_type: owner_type,
      object_name: objName,
    };
    if (doc_sub_type === undefined) {
      delete dataT.doc_sub_type;
    }
    if (affiliated_entity_id) {
      dataT["affiliated_entity_id"] = Number(affiliated_entity_id);
    }
    if (reference_id) {
      dataT["reference_id"] = Number(reference_id);
    }

    if (doc_type === "bank_statement" || doc_type === "wallet_declaration") {
      dataT["object_type"] = doc_type === "bank_statement" ? "bank" : "wallet_address";
      dataT["object_id"] = doc_id;
    }
    
    // Check if URL contains 'kybOnboarding' to determine which API to call
    const currentUrl = window.location.href;
    const isKybOnboarding = currentUrl.includes('kybOnboarding');
    
    let data;
    if (isKybOnboarding) {
      const response = await createKybUploadedDocApi(dataT);
      data = response.data;
    } else {
      const response = await uploadDocApi(dataT);
      data = response.data;
    }
    
    toast.success("Document saved");
    if (setSaveResponse) {
      setSaveResponse(data);
    }
  } catch (error) {
    errorReaderToast(error);
  }
};

export const roundNumber = (originalNumber, decimal = 4) => {
  const multiplier = Math.pow(10, decimal);
  const roundedNumber = Math.floor(originalNumber * multiplier) / multiplier;

  return formatNumberWithCommas(roundedNumber, decimal);
};

export const checklistSections = [
  {
    title: "Company Information",
    items: [
      { id: "company_coi", label: "COI" },
      { id: "company_gstin", label: "GSTIN" },
      { id: "company_aoa", label: "AOA" },
      { id: "company_moa", label: "MOA" },
      { id: "company_pan", label: "PAN" },
      { id: "company_bank_statements", label: "Bank Statements (can be multiple)" },
      { id: "company_financial_statement", label: "Latest Financial Statement" }
    ],
  },
  {
    title: "Key Person",
    items: [
      { id: "board_resolution", label: "Board Resolution" },
      { id: "board_authorized_signatories", label: "Authorized Signatories Individual docs" },
      { id: "director_video_verification", label: "Director Video Verification" },
    ],
  },
  {
    title: "Wallet Declaration",
    items: [
      { id: "wallet_declaration", label: "Wallet Declaration" },
      { id: "wallet_video", label: "Wallet Ownership Video (can be multiple)" },
    ],
  },
  {
    title: "FIU Registration",
    items: [
      { id: "fiu_docs", label: "Relevant Supporting docs" },
    ],
  },
];

export const formatNumberWithCommas = (amount, decimal = 4) => {
  return Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimal,
    maximumFractionDigits: decimal,
  }).format(amount);
};

export const handleMaxDateFortExternalAccTeam = () => {
  const today = new Date();
  const currentDay = today.getDate();

  let targetDate;

  // If current date is less than 4th: Max date is last day of 2 months prior
  if (currentDay < 4) {
    targetDate = new Date(today.getFullYear(), today.getMonth() - 2 + 1, 0);
  }
  // If current date is 4th or later: Max date is last day of 1 month prior
  else {
    targetDate = new Date(today.getFullYear(), today.getMonth(), 0);
  }

  // Format date to YYYY-MM-DD avoiding timezone issues
  const year = targetDate.getFullYear();
  const month = String(targetDate.getMonth() + 1).padStart(2, "0");
  const day = String(targetDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const uploadFileToS3 = async (formData) => {
  try {
    const response = await uploadFileApi(formData);
    return response.data;
  } catch (error) {
    errorReaderToast(error);
  }
};

/**
 * Reusable pagination handler for tables with next/previous URL support
 * @param {Object} pagination - Ant Design pagination object
 * @param {number} currentPage - Current page number from state
 * @param {Object} dataObject - Data object containing next and previous URLs
 * @param {Function} fetchHandler - Function to fetch data
 * @param {Function} setCurrentPage - State setter for current page
 * @param {Function} setPageSize - State setter for page size
 * @param {string} statusFilter - Optional status filter
 */
export const handlePaginationChange = (
  pagination,
  currentPage,
  dataObject,
  fetchHandler,
  setCurrentPage,
  setPageSize,
  statusFilter = ""
) => {
  const { current, pageSize: newPageSize } = pagination;

  // Determine if we're going forward or backward
  if (current > currentPage && dataObject?.next) {
    // Going to next page - use next URL
    fetchHandler(null, newPageSize, statusFilter, "", "", false, dataObject.next);
  } else if (current < currentPage && dataObject?.previous) {
    // Going to previous page - use previous URL
    fetchHandler(null, newPageSize, statusFilter, "", "", false, dataObject.previous);
  } else {
    // First page or page size change - calculate offset
    const nextPage = current === 0 || current === 1 ? 0 : (current - 1) * newPageSize;
    fetchHandler(nextPage, newPageSize, statusFilter);
  }

  setCurrentPage(current);
  setPageSize(newPageSize);
};

// Bank account mapping for different banks
export const bankAccountMapping = {
  "Axis Bank": "Carret's Axis Account Number",
  "Kotak Mahindra Bank": "Carret's Kotak Account Number", 
  "American Union Bank": "Carret's AU Account Number"
};

// Bank format configurations for exports
export const bankFormats = {
  "Axis Bank": {
    headers: [
      "Debit Account Number (Mandatory)", 
      "Transaction Amount (Mandatory)", 
      "Transaction Currency (Non-Mandatory)", 
      "Beneficiary Name (Mandatory)", 
      "Beneficiary Account Number (Mandatory)", 
      "Transaction Date (Mandatory)", 
      "Customer Reference Number (Mandatory)", 
      "Beneficiary Code (Non-Mandatory)"
    ],
    dataMapping: (record, bankAcc) => {
      return {
        "Debit Account Number (Mandatory)": bankAccountMapping[bankAcc] || bankAcc || "Carret's Account Number",
        "Transaction Amount (Mandatory)": record?.amount || "",
        "Transaction Currency (Non-Mandatory)": "INR",
        "Beneficiary Name (Mandatory)": record?.account?.full_name || "",
        "Beneficiary Account Number (Mandatory)": record?.primary_bank_account?.account_number || record?.bank_account_no || "",
        "Transaction Date (Mandatory)": moment().format("DD/MM/YYYY"),
        "Customer Reference Number (Mandatory)": record?.id || "",
        "Beneficiary Code (Non-Mandatory)": record?.primary_bank_account?.ifsc || record?.ifsc || ""
      };
    }
  },
  "Kotak Mahindra Bank": {
    headers: ["Payment Method Name", "Debit Account No / IBAN", "Remitter Name", "Payment Amount (Request)", "IFSC CODE", "Beneficiary Account No", "Beneficiary Name (Request)", "Payment Instructions 1", "CCY"],
    dataMapping: (record, bankAcc) => {
      const amount = parseFloat(record?.amount || 0);
      const paymentMethod = amount < 500000 ? "IMPS" : "RTGS";

      return {
        "Payment Method Name": paymentMethod,
        "Debit Account No / IBAN": bankAccountMapping[bankAcc] || bankAcc || "Carret's Account Number",
        "Remitter Name": "Carretx Technologies Private Limited",
        "Payment Amount (Request)": record?.amount || "",
        "IFSC CODE": record?.primary_bank_account?.ifsc || record?.ifsc || "",
        "Beneficiary Account No": record?.primary_bank_account?.account_number || record?.bank_account_no || "",
        "Beneficiary Name (Request)": record?.account?.full_name || "",
        "Payment Instructions 1": "Send to user",
        "CCY": "INR"
      };
    }
  },
  "American Union Bank": {
    headers: ["Payment Method Name", "Debit Account No / IBAN", "Remitter Name", "Payment Amount (Request)", "IFSC CODE", "Beneficiary Account No", "Beneficiary Name (Request)", "Payment Instructions 1", "CCY"],
    dataMapping: (record, bankAcc) => {
      const amount = parseFloat(record?.amount || 0);
      const paymentMethod = amount < 500000 ? "IMPS" : "RTGS";

      return {
        "Payment Method Name": paymentMethod,
        "Debit Account No / IBAN": bankAccountMapping[bankAcc] || bankAcc || "Carret's Account Number",
        "Remitter Name": "Carretx Technologies Private Limited",
        "Payment Amount (Request)": record?.amount || "",
        "IFSC CODE": record?.primary_bank_account?.ifsc || record?.ifsc || "",
        "Beneficiary Account No": record?.primary_bank_account?.account_number || record?.bank_account_no || "",
        "Beneficiary Name (Request)": record?.account?.full_name || "",
        "Payment Instructions 1": "Send to user",
        "CCY": "INR"
      };
    }
  }
};

export const statusOptions = [
  {id: "All Status", value: "" },
  { id: "Pending", value: "pending" }, 
  { id: "Success", value: "success" }, 
  { id: "Failed", value: "failed" }, 
  { id: "Withdrawal Initiated", value: "withdrawal_initiated" }
]

export const twowaySwapStatusOptions = [
  { id: "All Status", value: "" },
  { id: "Open", value: "open" },
  {id: 'Filled', value: 'filled' },
  {id: 'Cancelled', value: 'cancelled' },
]

export const twowaySwapSideOptions = [
  { id: "All Status", value: "" },
  { id: "Sell", value: "sell" },
  { id: "Buy", value: "buy" },
]


export const changeRequestStatusOptions = [
  {id: "All Status", value: "" },
  { id: "Initiated", value: "initiated" }, 
  { id: "Approved", value: "approved" }, 
  { id: "Cancelled", value: "cancelled" }, 
];

export const userTradeConfirmationStatusOptions = [
  { id: "All Status", value: "" },
  { id: "Pending", value: "pending" },
  { id: "Approved", value: "approved" },
  { id: 'cancelled', value: 'cancelled' },
];

export const actionTexts = {
  approve: "Are you sure you want to approve this transaction?",
  reject: "Are you sure you want to reject this transaction?",
  save: "Are you sure you want to save changes to this transaction?",
};

export const accountCreationSteps = [
  { title: "Update Account", step_key: "update_account" },
  { title: "Certificate of incorporation", step_key: "c_o_i" },
  { title: "GSTIN", step_key: "gstin" },
  { title: "Company PAN", step_key: "pan" },
  { title: "AOA", step_key: "aoa" },
  { title: "MOA", step_key: "moa" },
  { title: "Bank Account", step_key: "bank" },
  { title: "Key Person", step_key: "b_r" },
  { title: "Shareholding Pattern", step_key: "s_p" },
  { title: "UBO Declaration", step_key: "u_d" },
  { title: "Director List", step_key: "d_l" },
  { title: "Kyc Form", step_key: "kyc_form" },
  { title: "Wallet", step_key: "wallet" },
  { title: "Declaration", step_key: "declaration" },
];

export const kybOnboardingVerificationSteps = [
  { title: "Company Information", step_key: "company_info" },
  { title: "Key Personnel", step_key: "board_resolution" },
  // { title: "Address Verification", step_key: "address_verification" },
  // { title: "UBO Information", step_key: "ubo_info" },
  // { title: "Director Information", step_key: "director_info" },
  { title: "Wallet Declaration", step_key: "wallet_declaration" },
  { title: "FIU Registration", step_key: "fiu_registration" },
  { title: "Sanctions Declaration", step_key: "sanctions_declaration" },
  { title: "Declarations Checklist", step_key: "declarations_checklist" },
  { title: "Verification Summary", step_key: "verification_summary" },
  { title: "Upload Other Documents", step_key: "upload_other_documents" },
  { title: "Download Documents", step_key: "download_documents" },
];

export const individualKycVerificationSteps = [
  { title: "Personal Information", step_key: "personal_info" },
  { title: "Income Proof", step_key: "income_proof" },
  { title: "Wallet Details", step_key: "wallet_declaration" },
  { title: "Declarations", step_key: "sanctions_declaration" },
  { title: "Declarations Checklist", step_key: "declarations_checklist" },
  { title: "Verification Summary", step_key: "verification_summary" },
  { title: "Upload Other Documents", step_key: "upload_other_documents" },
  { title: "Download Documents", step_key: "download_documents" },
];

export const individualChecklistSections = [
  {
    title: "Personal Information",
    items: [
      { id: "individual_pan", label: "PAN" },
      { id: "individual_aadhaar", label: "Aadhaar" },
      { id: "individual_selfie", label: "Selfie" },
      { id: "individual_basic_details", label: "Basic Details" },
      {
        id: "individual_bank_statements",
        label: "Bank Statements (can be multiple)",
      },
    ],
  },
  {
    title: "Income Proof",
    items: [
      {
        id: "individual_income_proof",
        label: "ITR or Salary Slips (last 3 months)",
      },
    ],
  },
  {
    title: "Wallet Details",
    items: [
      { id: "wallet_video", label: "Wallet Ownership Videos" },
      { id: "wallet_declaration", label: "Wallet Address Declarations" },
    ],
  },
  {
    title: "Declarations",
    items: [
      {
        id: "individual_source_of_funds_declaration",
        label: "Source of Funds Declaration",
      },
      { id: "individual_pep_declaration", label: "PEP Declaration" },
    ],
  },
];

export const nonIndianKybOnboardingSteps = [
  { title: "Key Personnel Information", step_key: "key_personnel" },
  { title: "Company / Entity Information", step_key: "company_entity" },
  { title: "Intermediaries", step_key: "related_parties" },
  { title: "Company Documents", step_key: "company_documents" },
  { title: "Financial Statements", step_key: "financial_statements" },
  { title: "Whitelisted Crypto Addresses", step_key: "whitelisted_crypto" },
  { title: "Bank Details", step_key: "bank_details" },
  { title: "Compliance Declarations", step_key: "compliance_declarations" },
  { title: "Verification Summary", step_key: "verification_summary" },
];

export const missingTransactionRejectReasons = [
  { value: "incorrect_utr", label: "Incorrect UTR" },
  { value: "incorrect_amount", label: "Incorrect Amount" },
  { value: "deposit_not_found", label: "Deposit Not Found" },
  { value: "others", label: "Others" },
];

export const ADDR_VERIF_STATUS_OPTIONS = [
  { label: "All", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Geo Verified", value: "geo_verified" },
  { label: "Mismatch", value: "mismatch" },
  { label: "Address Proof Required", value: "address_proof_required" },
  { label: "Manual Review", value: "manual_review" },
  { label: "Verified", value: "verified" },
  { label: "Failed", value: "failed" },
];

export const eddRequestRejectionReasons = [
  { value: "Insufficient documentation provided", label: "Insufficient documentation provided" },
  { value: "Income verification failed", label: "Income verification failed" },
  { value: "Document quality issues", label: "Document quality issues" },
  { value: "Information mismatch in documents", label: "Information mismatch in documents" },
  { value: "others", label: "Others" },
]

export const statusChoice = [
  { value: "pending", label: "Pending" },
  { value: "verified", label: "Verified" },
  { value: "requires_manual_review", label: "Requires Manual Review" },
  { value: "verification_failed", label: "Verification Failed" },
  { value: "re_verification_required", label: "Re-Verification Required" },
];


export const kybOnboardingRejectionReasons = [
  { label: "Document not clear" ,value : "document_not_clear"},
  { label: "Information mismatch", value: "information_mismatch" },
  { label: "Incomplete documentation", value: "incomplete_documentation" },
  { label: "Invalid document", value: "invalid_document" },
  { label: "Other", value: "others" }
];

export const getLocalTimestampForFile = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`;
};

export const sanitizeFileName = (name) => {
  return String(name || "document").replace(/[\\/:*?"<>|]/g, "_");
};

export const downloadBlobAsFile = (blob, fileName) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = sanitizeFileName(fileName);
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
};

export const downloadUrlAsFile = async (url, fileName) => {
  if (!url) return;
  try {
    const res = await fetch(url);
    if (!res.ok) {
      toast.error(`Failed to fetch: ${fileName || url}`);
      return;
    }
    const blob = await res.blob();
    downloadBlobAsFile(blob, fileName);
  } catch (err) {
    toast.error(`Download error for: ${fileName || url}`);
  }
};

export const downloadManyUrlsAsZip = async ({ folderName, files = [] }) => {
  // files: [{ url, name }]
  // NOTE: requires deps:
  //   npm install jszip file-saver
  let JSZip, saveAs;
  try {
    JSZip = (await import("jszip")).default;
    // file-saver's default export is the saveAs function
    saveAs = (await import("file-saver")).default;
  } catch (e) {
    toast.error(
      "Missing dependency: Please install 'jszip' and 'file-saver' to enable ZIP downloads.\nRun: npm install jszip file-saver"
    );
    return;
  }

  const zip = new JSZip();
  const safeFolder = sanitizeFileName(folderName || `download_${getLocalTimestampForFile()}`);
  const folder = zip.folder(safeFolder);

  const valid = (Array.isArray(files) ? files : []).filter((f) => !!f?.url);

  await Promise.all(
    valid.map(async ({ url, name }) => {
      try {
        const res = await fetch(url);
        if (!res.ok) {
          toast.error(`Failed to fetch: ${name || url}`);
          return;
        }
        const blob = await res.blob();
        folder.file(sanitizeFileName(name), blob);
      } catch (err) {
        toast.error(`Download error for: ${name || url}`);
      }
    })
  );

  const zipBlob = await zip.generateAsync({ type: "blob" });
  saveAs(zipBlob, `${safeFolder}.zip`);
};

export const rfiAlertStatusActions = [
  { label: "Pending", value: "pending" },
  { label: "Dismiss", value: "dismiss" },
  { label: "Suspicious", value: "suspicious" },
]

export const getStatusColors = (status) => {
  const normalized = String(status || "")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .trim();

  switch (normalized) {
    case "verified":
    case "approved":
    case "success":
    case "completed":
    case "complete":
    case "credited":
    case "filled":
    case "passed":
    case "onboarded":
    case "fully onboarded":
      return { bg: "#f6ffed", color: "#52c41a" };
    case "no":
      return { bg: "#f6ffed", color: "#52c41a" };
    case "pending":
    case "initiated":
    case "in review":
    case "otp pending":
    case "uploaded":
    case "yes":
    case "in progress":
      return { bg: "#fffbe6", color: "#faad14" };
    case "rejected":
    case "failed":
    case "verification failed":
    case "cancelled":
      return { bg: "#fff1f0", color: "#ff4d4f" };
    case "flagged":
      return { bg: "#fff7e6", color: "#d46b08" };
    case "processing":
    case "submitted":
    case "otp verified":
      return { bg: "#e6f4ff", color: "#1677ff" };
    case "awaiting resubmission":
      return { bg: "#fff2e8", color: "#d4380d" };
    case "re kyc":
      return { bg: "#f9f0ff", color: "#722ed1" };
    case "not started":
    case "draft":
      return { bg: "#fafafa", color: "#8c8c8c" };
    case "active":
      return { bg: "#f6ffed", color: "#52c41a" };
    default:
      return { bg: "#fafafa", color: "#000" };
  }
};

/** Account type filter UI options for Transaction dashboard (no OTC / OTC API). */
export const ACCOUNT_TYPE_FILTER_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Retail", value: "retail" },
  { label: "TAAS", value: "taas" },
].filter((option) => option.value !== "otc" && option.value !== "otc_desk");

/** Maps UI account-type filter to API account_type value */
export const resolveAccountTypeParam = (accountType = "") => {
  if (!accountType || accountType === "all") return "";
  if (accountType === "retail") return "retail";
  if (accountType === "taas") return "taas_r,taas_r_user";
  return accountType;
};

/** Maps UI account-type filter to API client_type value */
export const resolveClientTypeParam = (
  accountType = "",
  allValue = "retail,taas_r,taas_r_user",
) => {
  if (!accountType || accountType === "all") return allValue;
  if (accountType === "retail") return "retail";
  if (accountType === "taas") return "taas_r,taas_r_user";
  return accountType;
};

export const isTaasAccountTypeFilter = (accountType = "") =>
  accountType === "taas";

export const CLIENT_TYPE_LABELS = {
  retail: "Retail",
  private: "Private",
  otc: "OTC API",
  taas_r: "TaaS R",
  remit: "Remit",
  taas_r_user: "Taas",
};

/** Account type filter on OTC Desk Admin pages (TaaS R removed). */
export const OTC_ACCOUNT_TYPE_OPTIONS = [
  { label: "OTC", value: "otc_desk" },
  { label: "OTC API", value: "otc" },
].filter((option) => option.value !== "taas_r");

/** Map UI account-type filter → API `client_type` for OTC dashboard calls. */
export const resolveOtcDashboardClientType = (accountType = "otc") => {
  if (accountType === "otc_desk") return "otc";
  return accountType || "otc";
};

export const isOtcDirectDeskAccountType = (accountType = "") =>
  accountType === "otc_desk";

export const formatClientTypeLabel = (clientType = "") =>
  CLIENT_TYPE_LABELS[clientType] || clientType;

export const normalizeClientTypes = (clientType) => {
  if (Array.isArray(clientType)) return clientType.filter(Boolean);
  return clientType ? [clientType] : [];
};

export const statusColors = (user_status) => {
  switch (user_status) {
    case "pending":
    case 'initiated':
    case 'processing':
      return "yellow";
    case "success":
    case "approved":
    case "verified":
    case "passed":
    case "completed":
      return "green";
    case "failed":
    case "rejected":
    case 'error':
    case "fail":
    case "cancelled":
      return "red";
    case "withdrawal_initiated":
    case 'warning': 
      return "orange";
    case "withdrawal":
    case "manual_review":
    case "needs_review":
      return "yellow";
    default:
      return "gray";
  }
};

//format to dd/mm/yyyy
export const formatDate = (date) => {
  if (!date) return '';
  const raw = String(date).trim();

  const dashYmd = /^\d{4}-\d{2}-\d{2}$/;
  const dashDmy = /^\d{2}-\d{2}-\d{4}$/;
  const slashDmy = /^\d{2}\/\d{2}\/\d{4}$/;

  if (dashYmd.test(raw)) {
    const [year, month, day] = raw.split('-');
    return `${day}/${month}/${year}`;
  }

  if (dashDmy.test(raw)) {
    const [day, month, year] = raw.split('-');
    return `${day}/${month}/${year}`;
  }

  if (slashDmy.test(raw)) return raw;

  return raw.replace(/-/g, '/');
};


/** Format: 2026/07/17 T\n14:24:56 */
export const formatExpiryDateTime = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  const match = raw.match(
    /^(\d{4})[-/](\d{2})[-/](\d{2})[T\s]+(\d{2}):(\d{2}):(\d{2})/,
  );
  if (!match) {
    return { dateLine: formatDate(raw) || raw, timeLine: null };
  }
  const [, year, month, day, hour, minute, second] = match;
  return {
    dateLine: `${year}/${month}/${day} T`,
    timeLine: `${hour}:${minute}:${second}`,
  };
};

export const tradeConfirmationAssetsOption = [
  { value: "USDT", label: "USDT" },
  { value: "BTC", label: "BTC" },
  { value: "ETH", label: "ETH" },
];

export const accountingRoleSwitch = [
  { value: "admin", label: "Admin" },
  { value: "fiu", label: "FIU" },
  { value: "ops", label: "OPS" },
];

export const fiuRoleSwitch = [
  { value: "admin", label: "Admin" },
  // { value: "fiu_user", label: "FIU User" },
  { value: "ops", label: "OPS" },
];

export const handleExport = async (downloadUrl) => {
  // if (isExporting) return;
  try {
    if (downloadUrl && typeof downloadUrl === 'string') {
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", ""); // Let browser determine filename from URL
      link.setAttribute("target", "_blank"); // Open in new tab as fallback
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.dismiss();
      toast.success("Download started successfully");
    } else {
      toast.dismiss();
      toast.error("Invalid download URL received");
    }
  } catch (error) {
    toast.dismiss();
    console.error("Export error:", error);
    errorReaderToast(error);
  }
  finally {
    // setIsExporting(false);
  }
};


export const stripAllSpaces = (value) => String(value ?? "").replace(/\s+/g, "");

// Filters input to allow only alphanumeric characters (letters and numbers)
export const filterAlphanumeric = (value) => {
  return String(value ?? "").replace(/[^a-zA-Z0-9]/g, "");
};

export const copyToClipboard = async (text, successMsg = "Copied to clipboard") => {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    if (typeof toast === 'function') toast.success(successMsg);
  } catch (err) {
    if (typeof toast === 'function') toast.error("Failed to copy");
  }
};

export const verifyMalware = async (
  objectName,
  retryCount = 0,
  maxRetries = 10,
  scanStatusApi = verifymalewareFileApi,
) => {
    try {
      const verifyResponse = await scanStatusApi({ object_key: objectName });
      const httpStatus = verifyResponse?.status;
      const responseData = verifyResponse?.data;
      const statusCode = responseData?.status;
      const message = responseData?.message;

      // Handle different response statuses
      if (httpStatus === 200 && statusCode === 'clean') {
        // File is clean, proceed
        toast.success(message || 'File passed security scan. You may proceed.');
        return true;
      }else if (httpStatus === 202 && statusCode === 'pending') {
          // File is still being scanned - retry after delay
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 3000)); // Wait 3 seconds before retry
            return await verifyMalware(objectName, retryCount + 1, maxRetries, scanStatusApi);
          } else {
            toast.error('File scan is taking too long. Please try again later.');
            throw new Error('File scan timeout');
          }
        } else {
        // Unknown response
        toast.error(message || 'Unknown response from security scan');
        throw new Error('Unknown scan response');
      }
    } catch (verifyError) {
      // Check if this is an axios error with a response (expected status codes)
      if (verifyError?.response) {
        const httpStatus = verifyError.response?.status;
        const responseData = verifyError.response?.data;
        const statusCode = responseData?.status;
        const message = responseData?.message;

        // Handle expected non-200 responses
        if (httpStatus === 404 && statusCode === 'not_found') {
          toast.error(message || 'Object not found in S3. It may have already been deleted.');
          throw new Error('File not found');
        } else if (httpStatus === 422 && statusCode === 'threat') {
          toast.error(message || 'Malware was detected in your file. It has been removed. Please upload a different file.');
        }
      }

      // If it's not one of the expected responses, show generic error
      console.error('Malware verification failed:', verifyError);
      toast.error('Malware verification failed. Please try again.');
      throw verifyError;
    }
  };

export const platformOptions = [
  { label: "All", value: "" },
  { label: "FIU", value: "FIU" },
  { label: "I4C", value: "I4C" },
  { label: "Fin Gate", value: "FINGATE" },
  { label: "Sahyog Portal", value: "SAHYOG_PORTAL" },
];

export const matchedUnmatchedOptions = [
  {label: "All", value: "" },
  { label: "Matched", value: true },
  { label: "Unmatched", value: false },
]

export const companyTypeList = [
  { label: 'Private Limited', value: 'private_limited' },
  { label: 'Public Limited', value: 'public_limited' },
  {
    label: 'Sole Proprietorship',
    value: 'sole_proprietorship',
  },
  {
    label: 'Partnership',
    value: 'partnership',
  },
  {
    label: 'Holding Company',
    value: 'holding_company',
  },
  {
    label: 'LLP',
    value: 'llp',
  },
  {
    label: 'NGO',
    value: 'ngo',
  },
  {
    label: 'Government Company',
    value: 'government_company',
  },
  {
    label: 'Other',
    value: 'other',
  },
];

export const normalizeListResponse = (payload) => {
  const body = payload?.data ?? payload ?? {};
  const rawResults = body.results;

  const results = Array.isArray(rawResults)
    ? rawResults
    : rawResults && typeof rawResults === "object"
      ? [rawResults]
      : [];

  return {
    count: body.count ?? results.length,
    results,
    next: body.next ?? null,
    previous: body.previous ?? null,
  };
};

export const SANCTIONS_FIELDS = [
  {
    key: 'dealings_with_high_risk_jurisdictions',
    detailsKey: 'dealings_with_high_risk_jurisdictions_details',
    label:
      '1. Does the company or any of its related parties incorporated, operates or has revenues and principal place of business with direct or indirect dealings in /with / through the listed countries / territories?',
    detailsLabel:
      'If Yes, please provide details for the company or its related parties in detail:',
  },
  {
    key: 'related_parties_in_high_risk_jurisdictions',
    detailsKey: 'related_parties_in_high_risk_jurisdictions_details',
    label:
      '2.Does the company have any beneficial owners, directors, officers, authorized signatories, partners (as applicable) and related parties are citizens, nationality and resides in any of the listed countries / territories?',
    detailsLabel:
      'If Yes, provide details including full name of the individual(s), role in the company, the country of citizenship, nationality and place of residence. If entity, provide the activities involved, country of operation and the relationship with the company:',
  },
  {
    key: 'trade_with_high_risk_jurisdictions',
    detailsKey: 'trade_with_high_risk_jurisdictions_details',
    label:
      '3. Does the company or any of its related parties deals, trades, transports any goods, services, technology, software, resources that originate from, or to / from / through the listed countries / territories?',
    detailsLabel:
      'If Yes, provide the activities involved, country of operation and the relationship with the company:',
  },
  {
    key: 'dealings_with_sanctioned_persons_or_entities',
    detailsKey: 'dealings_with_sanctioned_persons_or_entities_details',
    label:
      '4. Does the company or any of its related parties have any direct or indirect dealings with or through any Sanctions Persons or Sanctioned Entity?',
    detailsLabel:
      'If Yes, provide details including full name of the individual(s), role in the company, the country of citizenship, nationality and place of residence. If entity, provide the activities involved, country of operation and the relationship with the company:',
  },
  {
    key: 'arms_weapons_defence_business',
    detailsKey: 'arms_weapons_defence_business_details',
    label:
      '5. Does the company or any of its related parties directly or indirectly deal in products related to Arms, Weapons, pyrotechnics or defence industries?',
    detailsLabel:
      'If Yes, provide full name of the company(ies) and its business activities:',
  },
  {
    key: 'has_pep_associations',
    detailsKey: 'has_pep_associations_details',
    label:
      '6. Does the company have any beneficial owners, directors, officers, authorized signatories, partners (as applicable) and related parties who is a PEP (Politically Exposed Person).',
    detailsLabel:
      'If Yes, provide details including full name of the individual(s), role in the company, nationality and place of residence and their position or role in the government',
  },
]

export const INDIVIDUAL_FIELDS = [
  { key: "name", label: "Name" },
  { key: "dob", label: "DOB" },
  { key: "pan", label: "PAN" },
  { key: "address", label: "Address (Aadhaar)" },
  { key: "email", label: "Email" },
  {key: "aml_status", label: "AML Status"},
  {key: "kyc_status", label: "KYC Status"},
];

export const INSTITUTIONAL_FIELDS = [
  { key: "name", label: "Name" },
  { key: "dob", label: "DOB" },
  { key: "pan", label: "PAN" },
  { key: "registered_address", label: "Registered Address" },
  { key: "operating_address", label: "Operating Address" },
  { key: "email", label: "Email" },
  {key: "aml_status", label: "AML Status"},
  {key: "kyc_status", label: "KYC Status"},
];

/** Parent account card fields (no email / status rows) */
export const PARENT_ACCOUNT_FIELDS = [
  { key: "name", label: "Name" },
  { key: "dob", label: "DOB" },
  { key: "pan", label: "PAN" },
  { key: "registered_address", label: "Registered Address" },
  { key: "operating_address", label: "Operating Address" },
];

const ADDRESS_DISPLAY_KEYS = [
  "addressLine1",
  "addressLine2",
  "city",
  "state",
  "pincode",
];

/** Formats JSON address strings into "line1, line2, city, state, pincode" */
export const formatAddressDisplay = (addressValue) => {
  if (addressValue == null || addressValue === "" || addressValue === "—") {
    return "—";
  }

  let parsed = addressValue;
  if (typeof addressValue === "string") {
    try {
      parsed = JSON.parse(addressValue);
    } catch {
      return addressValue;
    }
  }

  if (typeof parsed !== "object" || Array.isArray(parsed)) {
    return String(addressValue);
  }

  const parts = ADDRESS_DISPLAY_KEYS.map((key) => parsed?.[key]).filter(Boolean);
  return parts.length ? parts.join(", ") : "—";
};
